import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Input,
  Button,
  Space,
  Select,
  Tooltip,
  Alert,
  Typography,
  Card,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  QuestionCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  setQuery,
  generateReport,
  clearReport,
  interpretQuery,
  clearQueryInterpretation,
} from '../../redux/slices/aiReportsSlice';
import { getPlaceholderText, validateQuery } from '../../utils/queryHelpers';
import styles from './ReportQueryBuilder.module.css';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;

const ReportQueryBuilder = ({ user }) => {
  const dispatch = useDispatch();
  const {
    currentQuery,
    reportStatus,
    queryInterpretation,
    error,
  } = useSelector((state) => state.aiReports);

  const [localQuery, setLocalQuery] = useState(currentQuery);
  const [outputFormat, setOutputFormat] = useState('html');
  const [priority, setPriority] = useState('normal');
  const [validationError, setValidationError] = useState(null);
  const [showInterpretation, setShowInterpretation] = useState(false);

  const isGenerating = ['generating', 'queued', 'processing'].includes(reportStatus);
  const userRole = user?.role || 'nurse';
  const isPhysician = userRole === 'doctor' || userRole === 'physician';

  // Update local state when Redux state changes
  useEffect(() => {
    setLocalQuery(currentQuery);
  }, [currentQuery]);

  // Handle query change
  const handleQueryChange = (e) => {
    const value = e.target.value;
    setLocalQuery(value);
    setValidationError(null);

    // Clear previous interpretation when query changes
    if (queryInterpretation) {
      dispatch(clearQueryInterpretation());
      setShowInterpretation(false);
    }
  };

  // Handle interpret query (preview)
  const handleInterpret = async () => {
    const validation = validateQuery(localQuery);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    setShowInterpretation(true);
    await dispatch(interpretQuery(localQuery));
  };

  // Handle generate report
  const handleGenerate = async () => {
    const validation = validateQuery(localQuery);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    // Clear previous report
    dispatch(clearReport());

    // Set query in Redux
    dispatch(setQuery(localQuery));

    // Generate report
    await dispatch(generateReport({
      query: localQuery,
      outputFormat,
      priority,
    }));
  };

  return (
    <Card className={styles.queryBuilder}>
      {/* Role-based banner */}
      {isPhysician && (
        <Alert
          message="Viewing data for your assigned patients only"
          type="info"
          showIcon
          className={styles.rbacBanner}
        />
      )}

      {/* Main query input */}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <div className={styles.headerRow}>
            <Title level={4}>Ask a question about your data</Title>
            <Tooltip title="Use natural language to query your healthcare data. AI will translate your question into a database query.">
              <QuestionCircleOutlined className={styles.helpIcon} />
            </Tooltip>
          </div>

          {/* Quick Report Buttons */}
          <div className={styles.quickReports}>
            <Text type="secondary">Common Reports: </Text>
            <Space wrap style={{ marginTop: 8 }}>
              <Button
                size="small"
                onClick={() => setLocalQuery("Show me vital signs for patient ")}
                disabled={isGenerating}
              >
                Vital Signs
              </Button>
              <Button
                size="small"
                onClick={() => setLocalQuery("Show me nurses notes for patient ")}
                disabled={isGenerating}
              >
                Nurses Notes
              </Button>
              <Button
                size="small"
                onClick={() => setLocalQuery("Show me I&O records for patient ")}
                disabled={isGenerating}
              >
                I&O
              </Button>
              <Button
                size="small"
                onClick={() => setLocalQuery("Show me all active patients in ")}
                disabled={isGenerating}
              >
                Patient List
              </Button>
              <Button
                size="small"
                onClick={() => setLocalQuery("Show me medpass report for patient ")}
                disabled={isGenerating}
              >
                MedPass
              </Button>
              <Button
                size="small"
                onClick={() => setLocalQuery("Show me physician notes for patient ")}
                disabled={isGenerating}
              >
                Physician Notes
              </Button>
            </Space>
            <Text
              type="secondary"
              style={{ fontSize: 12, fontStyle: 'italic', display: 'block', marginTop: 8 }}
            >
              💡 Tip: Use patient names (e.g., "John Doe") or MRN numbers for best results
            </Text>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <TextArea
            value={localQuery}
            onChange={handleQueryChange}
            placeholder={getPlaceholderText(userRole)}
            autoSize={{ minRows: 3, maxRows: 6 }}
            size="large"
            status={validationError ? 'error' : ''}
            disabled={isGenerating}
            className={styles.queryInput}
          />

          {validationError && (
            <Text type="danger" className={styles.validationError}>
              {validationError}
            </Text>
          )}

          <div className={styles.characterCount}>
            {localQuery.length} / 500 characters
          </div>
        </div>

        {/* Query interpretation preview */}
        {showInterpretation && queryInterpretation && (
          <Alert
            message="AI Understanding"
            description={
              <div>
                <Text strong>Intent: </Text>
                <Text>{queryInterpretation.intent_type}</Text>
                <br />
                <Text strong>Interpretation: </Text>
                <Text>{queryInterpretation.interpretation}</Text>
                {queryInterpretation.confidence && (
                  <>
                    <br />
                    <Text strong>Confidence: </Text>
                    <Text>{(queryInterpretation.confidence * 100).toFixed(0)}%</Text>
                  </>
                )}
              </div>
            }
            type="success"
            closable
            onClose={() => setShowInterpretation(false)}
          />
        )}

        {/* Error display */}
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            closable
            onClose={() => dispatch(clearReport())}
          />
        )}

        {/* Options row */}
        <Space size="middle" className={styles.optionsRow}>
          <div>
            <Text type="secondary">Output Format: </Text>
            <Select
              value={outputFormat}
              onChange={setOutputFormat}
              style={{ width: 120 }}
              disabled={isGenerating}
            >
              <Option value="html">HTML</Option>
              <Option value="pdf">PDF</Option>
              <Option value="both">Both</Option>
            </Select>
          </div>

          {userRole === 'nurse' && (
            <div>
              <Text type="secondary">Priority: </Text>
              <Select
                value={priority}
                onChange={setPriority}
                style={{ width: 120 }}
                disabled={isGenerating}
              >
                <Option value="low">Low</Option>
                <Option value="normal">Normal</Option>
                <Option value="urgent">
                  <ThunderboltOutlined /> Urgent
                </Option>
              </Select>
            </div>
          )}
        </Space>

        {/* Action buttons */}
        <Space size="middle">
          <Button
            type="primary"
            size="large"
            icon={<SearchOutlined />}
            onClick={handleGenerate}
            loading={isGenerating}
            disabled={!localQuery.trim()}
          >
            {isGenerating ? 'Generating...' : 'Generate Report'}
          </Button>

          <Button
            size="large"
            onClick={handleInterpret}
            disabled={isGenerating || !localQuery.trim()}
          >
            Preview Query
          </Button>

          {currentQuery && (
            <Button
              size="large"
              onClick={() => {
                setLocalQuery('');
                dispatch(clearReport());
                dispatch(setQuery(''));
              }}
              disabled={isGenerating}
            >
              Clear
            </Button>
          )}
        </Space>
      </Space>
    </Card>
  );
};

export default ReportQueryBuilder;
